import { useDispatch, useSelector } from '@redux';
import {
  TLayoutItem,
  addLayout,
  // setDefaultFilters,
  setDefaultShow,
  updateLayout,
} from '@redux/layouts/actions';
import { Layouts } from '@redux/layouts/slice';
import { TFilterRequest } from 'components/rest/Filter/FilterContent';
import { IRestColumnProps } from 'components/rest/Table/TableColumn';
import { uniqBy } from 'lodash';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export const useGetRootPath = () => {
  const location = useLocation();
  const rootPath = location.pathname.split('?')[0];
  const searchPath = new URLSearchParams(location.search);
  searchPath.delete('page');
  searchPath.delete('pageSize');
  const pathnameWithoutPageParams = `${rootPath}${
    searchPath.toString() ? `?${searchPath.toString()}` : ''
  }`;
  return {
    rootPath,
    pathname: pathnameWithoutPageParams,
    search: location.search,
  };
};

const getCurrentLayoutByPathId = (layouts, pathId) =>
  layouts?.find((i) => i.pathId === pathId);

const getCurrentLayout = (layouts, pathname) => {
  return (layouts || []).find((e) => {
    const layoutSearchParams = new URLSearchParams(
      e.customPath.substring(
        e.customPath.lastIndexOf('?'),
        e.customPath.length,
      ),
    );
    const currentSearchParams = new URLSearchParams(
      pathname.substring(pathname.lastIndexOf('?'), pathname.length),
    );
    const layoutName = layoutSearchParams.get('customLayout');

    return layoutName && layoutName === currentSearchParams.get('customLayout');
  });
};

export const useInitialFilters = () => {
  const { rootPath, pathname, search } = useGetRootPath();

  const layouts = useSelector<Layouts>((state) => state.layouts.layouts);
  const defaultLayouts = useSelector<Layouts>(
    (state) => state.layouts.defaultLayouts,
  );

  // const defaultFilters = useSelector<TFilterRequest[]>(
  //   (state) => state.layouts.defaultFilters[rootPath],
  // );
  const initialFilter = useMemo(() => {
    const currentSearchParams = new URLSearchParams(search);
    let currentCustomLayouts;
    if (currentSearchParams.get('customLayout')) {
      currentCustomLayouts = getCurrentLayout(layouts[rootPath], pathname);
    } else {
      currentCustomLayouts = getCurrentLayoutByPathId(
        defaultLayouts[rootPath],
        'ALL',
      );
    }

    return currentCustomLayouts;
  }, [pathname, layouts]);

  return initialFilter;
};

export const useShowColumns = (
  columns: IRestColumnProps[],
  ignoreDefault?: boolean,
) => {
  const { rootPath, pathname } = useGetRootPath();
  const layouts = useSelector<Layouts>((state) => state.layouts.layouts);
  const defaultShowColumns = useSelector<string[]>(
    (state) => state.layouts.defaultShows[rootPath],
  );

  const showColumns = useMemo(() => {
    const currentCustomLayouts = layouts[rootPath]?.find?.(
      (e: TLayoutItem) => e.customPath === pathname,
    );
    if (ignoreDefault) {
      return columns?.map((e) => e.name);
    }
    return (
      currentCustomLayouts?.showColumns ||
      defaultShowColumns ||
      columns.filter((e) => e.isDefault).map((e) => e.name)
    );
  }, [columns, pathname, defaultShowColumns, layouts]);

  return showColumns;
};

export const useShowLayout = () => {
  const layouts = useSelector<Layouts>((state) => state.layouts.layouts);
  const { rootPath } = useGetRootPath();
  return layouts[rootPath] || [];
};

export const useShowDefaultLayout = (defaultLayouts: TLayoutItem[]) => {
  const layouts = useSelector<Layouts>((state) => state.layouts.layouts);
  const defaultLayoutsRedux = useSelector<Layouts>(
    (state) => state.layouts.defaultLayouts,
  );
  const { rootPath, pathname } = useGetRootPath();
  const restDefaultLayouts = defaultLayouts.map((i) => {
    const existItem = defaultLayoutsRedux?.[rootPath]?.find(
      (item) => item.pathId === i.pathId,
    );
    return existItem?.pathId ? existItem : i;
  });
  const showLayouts = useMemo(() => {
    const currentCustomLayouts = layouts[rootPath] || [];
    return [
      ...uniqBy([...restDefaultLayouts], 'pathId'),
      ...currentCustomLayouts,
    ];
  }, [defaultLayouts, pathname, layouts, defaultLayoutsRedux]);
  return showLayouts || [];
};

export const useActiveDefaultTab = (defaultLayouts: TLayoutItem[]) => {
  const layouts = useSelector<Layouts>((state) => state.layouts.layouts);
  const { rootPath, pathname } = useGetRootPath();
  const showLayouts = useMemo(() => {
    const currentCustomLayouts = layouts[rootPath] || [];
    return [...defaultLayouts, ...currentCustomLayouts];
  }, [defaultLayouts, pathname, layouts]);
  return getCurrentLayout(showLayouts || [], pathname)?.title || 'All';
};

export const useActiveTab = () => {
  const layouts = useSelector<Layouts>((state) => state.layouts.layouts);
  const { rootPath, pathname } = useGetRootPath();
  return getCurrentLayout(layouts[rootPath], pathname)?.title || 'All';
};

export const useChangeShowLayout = () => {
  const layouts = useSelector<Layouts>((state) => state.layouts.layouts);
  const { rootPath, pathname, search } = useGetRootPath();
  const dispatch = useDispatch();
  const setDefaultShowColumns = useSetDefaultShowColumns();

  const changeShowLayout = (columns: string[]) => {
    const searchPath = new URLSearchParams(search);
    searchPath.delete('page');
    searchPath.delete('pageSize');
    const oldLayout = getCurrentLayout(layouts[rootPath], pathname);
    if (!oldLayout) {
      setDefaultShowColumns(columns);
      return;
    }

    dispatch(
      updateLayout({
        ...oldLayout,
        showColumns: columns,
      }),
    );
  };
  return changeShowLayout;
};

export const useAddShowLayout = () => {
  const { rootPath } = useGetRootPath();
  const dispatch = useDispatch();
  const addShowLayout = (title, columns?: string[]) => {
    const searchPath = new URLSearchParams();
    searchPath.delete('page');
    searchPath.delete('pageSize');
    const pathId = `${parseInt((new Date().getTime() / 1000).toFixed(0))}`;
    searchPath.set(
      'customLayout',
      `${parseInt((new Date().getTime() / 1000).toFixed(0))}`,
    );

    dispatch(
      addLayout({
        title,
        rootPath: rootPath,
        customPath: `${rootPath}?${searchPath.toString()}`,
        showColumns: columns,
        pathId,
      }),
    );
  };
  return addShowLayout;
};

export const useSetDefaultShowColumns = () => {
  const { rootPath } = useGetRootPath();
  const dispatch = useDispatch();

  const setDefaultShowColumns = (columns: string[]) => {
    dispatch(
      setDefaultShow({
        rootPath: rootPath,
        showColumns: columns,
      }),
    );
  };
  return setDefaultShowColumns;
};

export const useSaveFilter = () => {
  const layouts = useSelector<Layouts>((state) => state.layouts.layouts);
  const defaultLayouts = useSelector<Layouts>(
    (state) => state.layouts.defaultLayouts,
  );

  const { rootPath, pathname, search } = useGetRootPath();
  const dispatch = useDispatch();

  const saveFilter = (
    filter: {
      filters?: TFilterRequest[];
      q?: string;
      fieldSearch?: string;
    } = {},
    item?: TLayoutItem,
  ) => {
    const searchPath = new URLSearchParams(search);
    searchPath.delete('page');
    searchPath.delete('pageSize');
    if (filter?.filters) {
      searchPath.set('filters', JSON.stringify(filter?.filters, null, 0));
    }
    const searchStr = searchPath.toString() || '';
    const customPath = `${rootPath}${searchStr ? `?${searchStr}` : ''}`;
    if (item?.isDefault) {
      if (!item.pathId) return;
      const oldLayoutDefault = getCurrentLayoutByPathId(
        defaultLayouts?.[rootPath],
        item.pathId,
      );
      if (oldLayoutDefault) {
        dispatch(
          updateLayout({
            ...oldLayoutDefault,
            filters: filter?.filters,
            q: filter?.q,
            customPath,
            isDefault: true,
            fieldSearch: filter?.fieldSearch,
          }),
        );
      } else {
        dispatch(
          addLayout({
            ...item,
            filters: filter?.filters,
            q: filter?.q,
            customPath,
            isDefault: true,
            fieldSearch: filter?.fieldSearch,
          }),
        );
      }
    } else {
      const oldLayout = getCurrentLayout(layouts[rootPath], pathname);

      // if (!oldLayout) {
      //   dispatch(
      //     setDefaultFilters({
      //       rootPath,
      //       filters,
      //     }),
      //   );
      //   return;
      // }
      if (oldLayout) {
        dispatch(
          updateLayout({
            ...oldLayout,
            filters: filter?.filters,
            q: filter?.q,
            customPath: `${rootPath}?${searchPath.toString()}`,
          }),
        );
      }
    }
  };
  return saveFilter;
};
